import { Button, Chip, Stack, useTheme } from "@mui/material";
import { EllipsisIcon, FolderClosedIcon, Pencil } from "lucide-react";
import { type FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFolderEditMutation } from "web_myplann/Api/Document/useFolderEditMutation";
import type { DocumentTagResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { MyPlannInput } from "web_myplann/Shared/Components/MyPlannInput";
import { ActionsDrawerRedesign } from "web_myplann/Shared/Components/Redesign/ActionsDrawerRedesign";
import type { DrawerAction } from "web_myplann/Shared/Components/Redesign/ActionsDrawerRow";
import { RedesignListItemRow } from "web_myplann/Shared/Components/Redesign/RedesignListItemRow";

type Props = {
	item: DocumentTagResponseDTO;
};

export const FolderItemRedesign: FC<Props> = ({ item }) => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();

	const { mutate: renameFolder } = useFolderEditMutation();

	const [renameValue, setRenameValue] = useState(item.title);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);

	const actions: DrawerAction[] = [
		{
			key: "rename",
			label: t(Resources.Portfolio.List.FolderActionsDrawer.Actions.rename),
			icon: Pencil,
			onClick: () => setIsRenameDialogOpen(true),
		},
	];

	const handleRename = () => {
		renameFolder({
			requestContract: { titleToEdit: item.title, title: renameValue },
		});
		setIsRenameDialogOpen(false);
		setIsDrawerOpen(false);
	};

	return (
		<RedesignListItemRow
			icon={
				<FolderClosedIcon
					size={theme.iconSize.md}
					color={theme.palette.grey[500]}
				/>
			}
			title={item.title}
			onClick={() => navigate(Routing.Portfolio.FolderDetail.path(item.title))}
			rightSlot={
				<>
					{item.documentCount != null && (
						<Chip variant="tag" label={item.documentCount} />
					)}

					<EllipsisIcon
						size={theme.iconSize.sm}
						color={theme.palette.grey[400]}
						onClick={(event) => {
							event.stopPropagation();
							setIsDrawerOpen(true);
						}}
					/>

					<ActionsDrawerRedesign
						open={isDrawerOpen}
						onClose={() => setIsDrawerOpen(false)}
						title={item.title}
						concealed={isRenameDialogOpen}
						actions={actions}
					/>

					<MyPlannDrawer
						open={isRenameDialogOpen}
						onClose={() => {
							setIsRenameDialogOpen(false);
							setIsDrawerOpen(false);
							setRenameValue(item.title);
						}}
						title={t(
							Resources.Portfolio.List.FolderActionsDrawer.Actions.rename,
						)}
					>
						<Stack
							px={theme.spacingTokens.xl}
							pb={theme.spacingTokens.xl}
							gap={theme.spacingTokens.md}
						>
							<MyPlannInput
								label={t(
									Resources.Portfolio.List.DocumentActionsDrawer.Rename
										.folderRename,
								)}
								value={renameValue}
								onChange={(event) => setRenameValue(event.target.value)}
							/>
							<Button onClick={handleRename}>{t(Resources.Common.save)}</Button>
						</Stack>
					</MyPlannDrawer>
				</>
			}
		/>
	);
};
